<template>
  <div class="app">
    <t-navbar
      theme="sysadmin"
      :user="authUser"
      :nav-items="this.$getPrimaryNavItems('system')"
      v-model="closed"
    >
      <template #header>
        <t-nav-select
          v-if="mspAccountList"
          label="MSPs"
          data-test-id="navbar-customer-select"
          @selected="mspSelected"
          :closed="closed"
          :loading="loading"
          :options="mspAccountList"
          :initial-selected="initialMsp"
          theme="sysadmin"
        />
        <b-skeleton
          v-else
          class="mt-6"
          animation="wave"
          height="56px"
          width="100%"
        />
      </template>
    </t-navbar>
    <div
      class="app-content"
      v-if="this.selectedMspUser"
      :key="this.selectedMspUser.id"
    >
      <main>
        <b-container fluid="lg">
          <t-header
            :user="authUser"
            :user-links="userLinks"
            :product-links="productLinks"
            :version="version"
            :license="license"
            theme="sysadmin"
          />
          <router-view />
        </b-container>
      </main>
      <t-footer
        class="footer-image-bg"
        :version="appVersion"
        :logo="footerLogo"
        :nameservers="nameservers"
      />
      <t-modal />
      <t-toast />
      <t-alert />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { version } from "../../package";
import {
  PRODUCTS_ICON,
  PRODUCTS_TITLE,
  PRODUCTS_DESCRIPTION,
} from "@/constants";

import TNavbar from "@/components/TNavbar";
import TModal from "@/components/TModal";

export default {
  components: {
    TNavbar,
    TModal,
  },
  data() {
    return {
      version: null,
      license: null,
      appVersion: version,
      initialMsp: null,
      mspAccountList: null,
      closed: false,
      loading: true,
      nameservers: [],
      productLinks: [
        {
          title: PRODUCTS_TITLE.WEB,
          link: "#",
          active: true,
          icon: PRODUCTS_ICON.WEB,
          description: this.$t(PRODUCTS_DESCRIPTION.WEB),
          versionName: "- Errigal",
        },
      ],
      userLinks: [],
      footerLogo:
        this.$userPrefs.getCurrentUi().logo ||
        require("@/assets/branding/titanhq-logo.svg"),
    };
  },
  computed: {
    ...mapGetters("authentication", ["selectedMspUser", "authUser"]),
    ...mapGetters("msps", ["msps"]),
    ...mapGetters("support", ["support"]),
    ...mapGetters("userInterface", ["interface"]),
  },
  watch: {
    msps() {
      this.mspAccountList = this.cleanMspAccounts(this.msps);
    },
    support() {
      this.setSupportLinks(this.support);
    },
  },
  methods: {
    ...mapActions("support", ["fetchSupport"]),
    ...mapActions("authentication", ["setUser", "setMspUser", "setMspId"]),
    ...mapActions("msps", ["fetchMsps", "deleteMsp"]),
    ...mapActions("userInterface", ["fetchInterfaceBatch", "fetchInterface"]),
    ...mapActions("users", ["logoutUser"]),
    toggleNavigation() {
      this.closed = !this.closed;
    },
    onWindowResize(matchMedia) {
      if (matchMedia.matches && this.closed !== true) {
        this.closed = true;
      }

      if (!matchMedia.matches && this.closed !== false) {
        this.closed = false;
      }
    },
    async mspSelected(msp) {
      this.setMspUser(msp);
      this.setMspId(msp.id);
      localStorage.setItem("selectedMsp", msp.id);
      localStorage.setItem("selectedMspName", msp.name);
      if (msp.AllMsps) {
        this.$router.push("/system/overview");
      } else if (this.$route.path !== "/msp/overview") {
        await this.fetchInterface({ mspId: msp.id });
        this.$router.push("/msp/overview");
      }
    },
    setSelectedMspUser() {
      if (this.selectedMspUser?.id) {
        this.initialMsp = this.selectedMspUser.id;
        return;
      }

      const storedselectedMspID = localStorage.getItem("selectedMsp") || null;
      const filteredUserList = this.mspAccountList.filter(
        (msp) => msp.id === parseInt(storedselectedMspID)
      );

      if (storedselectedMspID && filteredUserList.length) {
        this.setMspUser(filteredUserList[0]);
        this.setMspId(filteredUserList[0].id);
        this.initialMsp = storedselectedMspID;
      } else {
        this.setMspUser(this.mspAccountList[0]);
        this.setMspId(this.mspAccountList[0].id);
        this.initialMsp = this.mspAccountList[0].id;
        localStorage.setItem("selectedMsp", this.mspAccountList[0].id);
      }
    },
    cleanMspAccounts(accounts) {
      const allAccounts = accounts
        .map((acct) => {
          return {
            ...acct,
            name: acct.name,
            testID: `navbar-customer-select-${acct.name
              .toLowerCase()
              .replace(/\s/g, "-")}`,
            AllMsps: false,
            short: this.$getAccountShortName(acct.name),
          };
        })
        .sort((a, b) =>
          a.name.toLowerCase() > b.name.toLowerCase()
            ? 1
            : b.name.toLowerCase() > a.name.toLowerCase()
            ? -1
            : 0
        );

      return [
        { name: "All MSPs", short: "AC", id: 0, AllMsps: true },
        ...allAccounts,
      ];
    },
    setSupportLinks(support) {
      this.userLinks = [
        {
          title: this.$t("Security"),
          link: "/profile/security",
          icon: "gear",
        },
      ];

      if (support.link) {
        this.userLinks.push({
          title: this.$t("Support"),
          link: this.support.link,
          icon: "life-preserver",
        });
      }

      if (support.email) {
        this.userLinks.push({
          title: this.$t("Contact"),
          link: `mailto:${this.support.email}`,
          icon: "chat-left",
        });
      }
    },
  },
  beforeCreate() {
    const userToken = this.$userPrefs.getCurrentToken();
    if (!userToken) this.$router.push("/");
    this.$http.setAuthorisationToken(userToken);
  },
  beforeMount() {
    this.setMspUser({
      name: "All MSPs",
      short: "AC",
      id: 0,
      AllMsps: true,
      license: "ABC-123",
    });
    this.setMspId(0);
  },
  async created() {
    if (!this.msps.length) await this.fetchMsps();
    await this.fetchSupport(false);
    this.setSupportLinks(this.support);

    this.mspAccountList = this.cleanMspAccounts(this.msps);

    this.loading = false;

    await this.fetchInterfaceBatch();
    // this.nameservers = this.interface.nameservers;
    this.version = String(this.interface.system.version);
    this.license = String(this.interface.system.license_number);
  },
};
</script>

<style lang="scss" scoped>
.app {
  display: flex;
  height: 100vh;
  overflow: hidden;

  &-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow-y: auto;

    footer {
      margin-top: auto;
    }
  }

  .app-content {
    background-color: $bg-light;
  }
}

@media only screen and (max-width: 1200px) {
  .app {
    &-content {
      margin-left: 3.5rem;
    }
  }
}
</style>
